/**
 * ConfirmDialog Component
 *
 * Reusable confirmation dialog for destructive actions
 * - Warning styling for destructive actions
 * - Customizable title, message, and button text
 * - ESC to cancel
 */

import React from "react";
import PropTypes from "prop-types";
import { Modal } from "../Modal";
import { Button } from "../Button";
import { FaExclamationTriangle } from "react-icons/fa";
import "./ConfirmDialog.scss";

export const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  loading = false,
  showCancel = true,
}) => {
  const handleConfirm = () => {
    onConfirm();
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="small"
      showCloseButton={false}
    >
      <div className="confirm-dialog">
        <div
          className={`confirm-dialog__icon confirm-dialog__icon--${variant}`}
        >
          <FaExclamationTriangle />
        </div>
        <h3 className="confirm-dialog__title">{title}</h3>
        <p className="confirm-dialog__message">{message}</p>
        <div className="confirm-dialog__actions">
          {showCancel && (
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              fullWidth
            >
              {cancelText}
            </Button>
          )}
          <Button
            variant={variant}
            onClick={handleConfirm}
            loading={loading}
            fullWidth
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

ConfirmDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  showCancel: PropTypes.bool,
  variant: PropTypes.oneOf(["danger", "primary"]),
  loading: PropTypes.bool,
};

export default ConfirmDialog;
